import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useApp } from '@/contexts/AppContext';
import { ArrowRight, ArrowLeft, X, Sparkles, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

const STEPS = [
  {
    route: '/lens',
    title: 'Anomaly detected in Command Center',
    body: 'APEX Lens detected a governance anomaly. A dev-origin container is running in production payments. Click to investigate.',
    highlight: 'pattern-intelligence',
  },
  {
    route: '/lens/investigations',
    title: 'One investigation, one connected story',
    body: 'INV-2026-042 ties together image provenance, IAM behavior, cross-environment trust, missing evidence, and PCI data exposure — all in one timeline.',
    highlight: 'investigation-INV-2026-042',
  },
  {
    route: '/lens/containers',
    title: '127,000 → 9 actions',
    body: 'Watch how runtime, environment, sensitivity, and provenance context eliminate noise. The first action is your dev-origin image in production.',
    highlight: 'container-waterfall',
  },
  {
    route: '/lens/identity',
    title: '847 granted. 12 used. 1.4% effective.',
    body: 'The IAM role running this container has AdministratorAccess. APEX generated the least-privilege replacement policy. 94% attack surface reduction.',
    highlight: 'identity-lambda-payments-processor',
  },
  {
    route: '/lens/environment',
    title: '201 days. Two 2am traversals. PCI data accessed.',
    body: 'A cross-environment IAM trust path from dev to the production payments database — never approved, exercised twice outside business hours.',
    highlight: 'env-finding-ENV-2',
  },
  {
    route: '/lens/missing-evidence',
    title: 'When evidence stops, APEX notices',
    body: 'Config evaluations stopped 9 days ago. No authorized explanation in CloudTrail. This is Missing Evidence Detection.',
    highlight: 'missing-evidence-ME-1',
  },
  {
    route: '/lens/evidence',
    title: 'Signed. Hashed. Audit-ready.',
    body: 'Every finding has been assembled into a signed evidence packet. Every claim traces to an immutable CloudTrail record.',
    highlight: 'evidence-packet-INV-2026-042',
  },
  {
    route: '/lens/remediation',
    title: 'Proves the problem. Tells you what to do.',
    body: 'Four actions. 3.5 hours. $263K of governance debt eliminated. Create tickets, generate guardrails, or assign owners — from here.',
    highlight: 'remediation-Payments-Team',
  },
];

const TOTAL = STEPS.length;

export default function DemoMode() {
  const { demoOpen, setDemoOpen, demoStep, setDemoStep, setPartnerModalOpen } = useApp();
  const navigate = useNavigate();

  useEffect(() => {
    if (demoOpen && demoStep > 0 && demoStep <= TOTAL) {
      const step = STEPS[demoStep - 1];
      navigate(step.route);
    }
  }, [demoOpen, demoStep, navigate]);

  const close = () => {
    setDemoOpen(false);
    setDemoStep(0);
  };

  const start = () => setDemoStep(1);
  const next = () => {
    if (demoStep < TOTAL) setDemoStep(demoStep + 1);
    else setDemoStep(TOTAL + 1);
  };
  const back = () => {
    if (demoStep > 1) setDemoStep(demoStep - 1);
    else setDemoStep(0);
  };

  const requestAccess = () => {
    setDemoOpen(false);
    setDemoStep(0);
    setTimeout(() => setPartnerModalOpen(true), 200);
  };

  return (
    <AnimatePresence>
      {demoOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className={`fixed inset-0 bg-slate-900/25 z-[9998] ${demoStep === 0 ? '' : 'pointer-events-none'}`}
            onClick={demoStep === 0 ? close : undefined}
          />

          {/* Step 0 — Opening modal */}
          {demoStep === 0 && (
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              data-testid="demo-modal-opening"
              className="fixed inset-0 z-[10000] flex items-center justify-center p-4"
            >
              <div className="bg-apex-shell text-white rounded-xl border border-slate-700 shadow-2xl max-w-lg w-full overflow-hidden grain">
                <div className="px-6 py-5 border-b border-slate-800 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-apex-lens" />
                    <span className="text-[10px] uppercase tracking-wider text-slate-400">Guided Tour</span>
                  </div>
                  <button onClick={close} data-testid="demo-close" className="text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>
                </div>
                <div className="px-6 py-7">
                  <h2 className="text-2xl font-bold tracking-tight">APEX Lens — Guided Demo</h2>
                  <p className="text-sm text-slate-300 mt-1">Follow the Stratum Payments incident end to end</p>
                  <p className="text-sm text-slate-400 mt-4 leading-relaxed">
                    This guided tour walks through a real governance failure — a dev-origin container running in a production payments environment — across every APEX Lens module.
                  </p>
                  <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-800 border border-slate-700 text-xs text-slate-300">
                    <span className="w-1.5 h-1.5 rounded-full bg-apex-lens animate-pulse" />
                    Estimated time: 8 minutes · 8 stops
                  </div>
                </div>
                <div className="px-6 py-4 border-t border-slate-800 bg-slate-900/50 flex justify-end">
                  <Button onClick={start} data-testid="demo-begin" className="bg-apex-lens hover:bg-apex-lens-dark text-white gap-1.5">
                    Begin Investigation
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </motion.div>
          )}

          {/* Mobile progress bar — top of viewport */}
          {demoStep >= 1 && demoStep <= TOTAL && (
            <motion.div
              initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
              data-testid="demo-mobile-progress"
              className="md:hidden fixed top-0 inset-x-0 z-[10001] bg-apex-shell text-white border-b border-slate-700"
            >
              <div className="px-4 py-2 flex items-center gap-3">
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-apex-lens text-white text-[10px] font-bold tabular shrink-0">{demoStep}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-wider text-slate-400 font-mono">Step {demoStep} of {TOTAL}</div>
                  <div className="text-xs font-semibold truncate">{STEPS[demoStep - 1].title}</div>
                </div>
                <button onClick={close} data-testid="demo-mobile-close" className="text-slate-400 hover:text-white shrink-0"><X className="w-4 h-4" /></button>
              </div>
              <div className="h-1 bg-slate-800 overflow-hidden">
                <motion.div
                  className="h-full bg-apex-lens"
                  initial={{ width: 0 }}
                  animate={{ width: `${(demoStep / TOTAL) * 100}%` }}
                  transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                />
              </div>
            </motion.div>
          )}

          {/* Steps 1..TOTAL — bottom sheet tooltip */}
          {demoStep >= 1 && demoStep <= TOTAL && (
            <motion.div
              key={demoStep}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 30 }}
              transition={{ ease: [0.16, 1, 0.3, 1] }}
              data-testid={`demo-step-${demoStep}`}
              className="fixed left-3 right-3 sm:left-auto sm:right-6 sm:bottom-24 sm:max-w-md bottom-20 z-[10000]"
            >
              <div className="bg-apex-shell text-white rounded-xl border border-slate-700 shadow-2xl overflow-hidden grain">
                <div className="px-5 py-4 border-b border-slate-800 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-apex-lens text-white text-[10px] font-bold tabular">{demoStep}</span>
                    <span className="text-[10px] uppercase tracking-wider text-slate-400">Step {demoStep} of {TOTAL}</span>
                  </div>
                  <button onClick={close} data-testid="demo-skip" className="text-slate-400 hover:text-white text-xs">Skip tour</button>
                </div>
                <div className="px-5 py-4">
                  <h3 className="text-base font-bold leading-tight">{STEPS[demoStep - 1].title}</h3>
                  <p className="text-sm text-slate-300 leading-relaxed mt-2">{STEPS[demoStep - 1].body}</p>
                </div>
                <div className="px-5 py-3 border-t border-slate-800 bg-slate-900/50 flex items-center justify-between gap-2">
                  <Button onClick={back} variant="ghost" size="sm" data-testid="demo-back" className="text-slate-300 hover:text-white hover:bg-slate-800 gap-1">
                    <ArrowLeft className="w-3.5 h-3.5" />
                    Back
                  </Button>
                  <div className="flex items-center gap-1">
                    {STEPS.map((_, i) => (
                      <span key={i} className={`block h-1 rounded-full transition-all ${i + 1 === demoStep ? 'bg-apex-lens w-6' : i + 1 < demoStep ? 'bg-apex-lens/60 w-2' : 'bg-slate-700 w-2'}`} />
                    ))}
                  </div>
                  <Button onClick={next} size="sm" data-testid="demo-next" className="bg-apex-lens hover:bg-apex-lens-dark text-white gap-1">
                    {demoStep === TOTAL ? 'Finish' : 'Next'}
                    <ArrowRight className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </motion.div>
          )}

          {/* Final wrap-up overlay */}
          {demoStep === TOTAL + 1 && (
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              data-testid="demo-end"
              className="fixed inset-0 z-[10000] flex items-center justify-center p-4"
            >
              <div className="bg-apex-shell text-white rounded-xl border border-slate-700 shadow-2xl max-w-lg w-full overflow-hidden grain">
                <div className="px-6 py-7 text-center">
                  <div className="mx-auto w-14 h-14 rounded-full bg-apex-lens/15 border border-apex-lens flex items-center justify-center mb-4">
                    <CheckCircle2 className="w-7 h-7 text-apex-lens" />
                  </div>
                  <h2 className="text-xl font-bold tracking-tight">Detected. Investigated. Evidenced. Remediated.</h2>
                  <p className="text-sm text-slate-300 mt-3 leading-relaxed max-w-sm mx-auto">
                    You just saw a complete cloud governance failure resolved in one platform. No other tool connects these dots.
                  </p>
                </div>
                <div className="px-6 py-4 border-t border-slate-800 bg-slate-900/50 flex flex-col sm:flex-row gap-2 sm:justify-end">
                  <Button onClick={close} variant="ghost" className="text-slate-300 hover:text-white hover:bg-slate-800">Close</Button>
                  <Button onClick={requestAccess} data-testid="demo-request-access" className="bg-apex-lens hover:bg-apex-lens-dark text-white gap-1.5">
                    Request Design Partner Access
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </motion.div>
          )}
        </>
      )}
    </AnimatePresence>
  );
}
